"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProductsCarouselConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const priorityTitles = ["Retatrutide", "GHK-Cu", "MOTS-c", "Semax", "Selank", "PT-141", "Kisspeptin-10", "Melanotan II", "CJC-1295", "Ipamorelin", "BPC-157", "TB-500 (Thymosin Beta-4)", "Tesamorelin", "IGF-1 LR3", "Epithalon", "KPV"];
  const sortedProducts = React.useMemo(() => {
    if (!products) return [];
    const priorityItems = [];
    const otherItems = [];
    const productMap = new Map(products.map(p => [p.title.toLowerCase(), p]));
    priorityTitles.forEach(title => {
      const match = productMap.get(title.toLowerCase());
      if (match) {
        priorityItems.push(match);
        productMap.delete(title.toLowerCase());
      }
    });
    productMap.forEach(product => {
      otherItems.push(product);
    });
    return [...priorityItems, ...otherItems];
  }, [products]);
  if (loading) {
    return <Carousel opts={{
      align: "start"
    }} className="w-full">
        <CarouselContent>
          {[...Array(4)].map((_, i) => <CarouselItem key={i} data-index={i} className="md:basis-2/3 lg:basis-1/2 pl-4">
              <Card className="rounded-sm border-none shadow-sm overflow-hidden h-full">
                <CardContent className="p-8 flex flex-col gap-4 h-full">
                  <div className="w-full aspect-square bg-muted/20 animate-pulse rounded-sm" />
                  <div className="h-6 bg-muted/20 animate-pulse rounded-sm w-3/4" />
                  <div className="h-4 bg-muted/20 animate-pulse rounded-sm w-1/2" />
                  <div className="mt-auto h-10 bg-muted/20 animate-pulse rounded-sm w-full" />
                </CardContent>
              </Card>
            </CarouselItem>)}
        </CarouselContent>
      </Carousel>;
  }
  if (error || products.length === 0) {
    return <div data-section-id="155828" className="text-muted-foreground text-center p-8 bg-muted rounded-sm border border-border">
        <p>No research compounds are currently listed for display.</p>
      </div>;
  }
  return <Carousel opts={{
    align: "start"
  }} className="w-full">
      <CarouselContent className="-ml-6">
        {sortedProducts.map((product, i) => <CarouselItem key={product.id} data-index={i} className="pl-6 md:basis-2/3 lg:basis-1/2">
            <Card className="rounded-sm border border-border shadow-sm bg-card text-card-foreground h-full flex flex-col overflow-hidden transition-all hover:shadow-md duration-300">
              <CardContent className="p-8 flex flex-col flex-grow gap-4">
                <Link to={product.link} className="block group">
                  <AspectRatio ratio={1} className="bg-muted rounded-sm overflow-hidden">
                    {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center bg-accent/10 text-accent">
                        <span className="text-xs font-sans font-medium uppercase tracking-tighter">No Image</span>
                      </div>}
                  </AspectRatio>
                </Link>
                
                <div className="flex flex-col flex-grow">
                  <Link to={product.link} className="hover:text-primary transition-colors">
                    <h3 className="font-default font-medium text-xl line-clamp-2 leading-tight tracking-tight">
                      {product.title}
                    </h3>
                  </Link>
                  
                  {product.price !== undefined && <div className="mt-2 mb-4">
                      <span className="text-xl font-medium text-foreground">
                        ${product.price.toFixed(2)}
                      </span>
                    </div>}
                  
                  <div className="mt-auto pt-4">
                    <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="bg-primary text-primary-foreground w-full rounded-sm font-sans text-lg font-medium uppercase tracking-wider py-5 shadow-sm hover:bg-primary/90 transition-colors" text="Add to Research" showQuantity={false} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </CarouselItem>)}
      </CarouselContent>
      <div className="hidden md:block">
        <CarouselPrevious className="bg-background text-foreground hover:bg-accent border border-border shadow-sm -left-6 size-12" />
        <CarouselNext className="bg-background text-foreground hover:bg-accent border border-border shadow-sm -right-6 size-12" />
      </div>
    </Carousel>;
}
export default function SalesBoost() {
  const wp_query_params = {
    per_page: 20,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="trustbar" className="bg-background text-foreground py-12 md:py-16 overflow-hidden border-y border-border">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          
          <div className="w-full lg:w-1/3 space-y-6 text-center lg:text-left">
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-sm bg-primary/10 text-primary text-sm font-sans font-semibold uppercase tracking-widest mb-2">
              Premium Research Catalog
            </div>
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-foreground leading-[1.1]">
              Scientific Integrity. Unmatched Purity.
            </h2>
            <p className="text-foreground/90 font-sans text-base md:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Signal Source One provides high-grade research peptides, meticulously synthesized and third-party verified for 99%+ batch purity. Engineered for clinical precision and laboratory reliability.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <div className="flex items-center gap-2 text-sm font-sans font-medium uppercase tracking-widest text-foreground">
                <div className="size-2.5 rounded-full bg-primary" />
                99%+ Batch Purity
              </div>
              <div className="hidden sm:block w-px h-4 bg-border" />
              <div className="flex items-center gap-2 text-sm font-sans font-medium uppercase tracking-widest text-foreground">
                <div className="size-2.5 rounded-full bg-primary" />
                Verified COA
              </div>
            </div>
          </div>

          <div className="w-full lg:w-2/3 relative">
            <EcommerceProductsProvider wp_query={{
            per_page: 20,
            order: "desc",
            orderby: "date"
          }} className="products-carousel-wrapper">
              <ProductsCarouselConsumer />
            </EcommerceProductsProvider>
          </div>
          
        </div>
      </div>
    </section>;
}